/**
 * Creates an URI querystring out of an object of params and encodes the values properly.
 * Note: The leading question mark is part of the result.
 *
 * The result will look like '?foo=bar&baz=blubb'
 *
 * @param {Object<string, string>} params
 * @return {string}
 */
export const createQueryString = (params = {}) => {
    const entries = Object.entries(params)

    return entries.length ? '?' + entries.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&') : ''
}

/**
 * Checks the given string for a leading slash character, adds a slash if not present, and returns the string.
 *
 * @param {string} str - An arbitrary string (default: empty string)
 * @returns {string} A string with a leading slash character
 */
export function leadingSlash(str = '') {
    return str.indexOf('/') === 0 ? str : '/' + str
}

/**
 * Checks the given string for a leading hash character, adds a hash if not present, and returns the string.
 *
 * @param {string} str - An arbitrary string (default: empty string)
 * @returns {string} A string with a leading hash character
 */
export function leadingHash(str = '') {
    return str.indexOf('#') === 0 ? str : '#' + str
}

/**
 * Parse url search parameter string to get an easy to
 * use object.
 *
 * @private
 * @param {string} queryString - The url query string
 * @returns {Object} The url query params as object map
 */
function parseQueryString(queryString) {
    return queryString.split(/&/).reduce((parameters, param) => {
        const [name, value] = param.split(/=/).map(entry => entry.toLowerCase())
        if (!parameters[name]) {
            parameters[name] = []
        }
        if (value) {
            parameters[name].push(decodeURIComponent(value.replace(/\+/g, ' ')))
        }
        return parameters
    }, {})
}

/**
 * Get url search parameters.
 *
 * @example
 * window.location.href = 'foo.html?foo=bar&baz=blubb'
 * getQueryParams() === { foo: 'bar', baz: 'blubb'}
 *
 * @returns {Object} The url query params as object map
 */
export function getQueryParams() {
    const { href } = window.location
    const idx = href.indexOf('?')

    if (idx !== -1) {
        return parseQueryString(href.slice(idx + 1))
    }
    return {}
}

/**
 * Opens the given url in a new window (newWindow=true) or redirects browser to the gven url
 * See {@link https://developer.mozilla.org/docs/Web/API/Window/open|window.open} and {@link https://developer.mozilla.org/docs/Web/API/Location/replace|window.open}
 * @param {string} url - the url redirect/open
 * @param {boolean} newWindow - switch to open the url in a new window, or the current one (default: false)
 * @returns {Window} The window which was created in the case of newWindow=true, null otherwise.
 */
export const redirect = (url, newWindow = false) =>
    newWindow ? window.open(url, '_blank') : window.location.assign(url)

/**
 * Get the current url without the fragment part
 * @returns {string} the current url without the fragment part
 */
export const getLocationWithoutFragment = () => window.location.href.split('#')[0]
